<template lang="pug">
.base-select-wrapper
  .base-select-backdrop.fixed.inset-0.z-40(v-if="isVisible", @click="close")
  .base-select.relative.flex.border.border-transparent.cursor-pointer(ref="selectRef", :class="{ 'select-disable': disabled }", @click="onClick")
    template(v-if="options.size")
      slot(v-if="selectedOption", name="current", :label="selectedOption.label", :value="selectedOption.value", :data="selectedOption.data")
        span.self-center.truncate {{ selectedOption.label }}
      span.base-select-placeholder.self-center(v-else) {{ placeholder || $t('common.select') }}
      span.ms-auto &nbsp
      .base-select-arrow.self-center.ms-1.transition.text-gray-400.text-sm(v-if="!disabled", :class="['origin-center', isVisible ? 'rotate-180' : '']")
        img(class="w-[1em]", src="assets/images/common/frame_icon_dropdown_normal.png", alt="arrow")
    .base-option-container.fixed.z-50.shadow-md(v-show="isVisible", :style="optionContainerStyle")
      .overflow-auto(v-scroll-gradient, :style="[`max-height: ${effectiveMaxHeight};`]")
        slot
</template>

<script setup>
import { ref, computed, provide, inject, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number, Object, Array],
    default: null,
  },
  placeholder: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  maxHeight: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue', 'change', 'blur', 'focus'])

const inputRef = inject('Input', null)
const formContext = inject('Form', null)

const selectRef = ref(null)
const options = ref(new Map())
const isVisible = ref(false)
const length = ref(0)

const selectedValue = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const selectContext = {
  registerOption: (option) => {
    options.value.set(option.value, option)
  },

  unregisterOption: (value) => {
    options.value.delete(value)
  },

  onSelect: (optionValue) => {
    selectedValue.value = optionValue

    // emit('update:modelValue', optionValue)
    emit('change', optionValue)
    isVisible.value = false
  },

  isSelected: (value) => {
    return selectedValue.value === value
  },

  selectedValue: selectedValue,
}

const selectedOption = computed(() => {
  return options.value.get(selectedValue.value)
})

const effectiveMaxHeight = computed(() => {
  return props.maxHeight ? `min(${length.value}px, ${props.maxHeight})` : `${length.value}px`
})

const optionContainerStyle = ref({})

const calculateOptionArea = () => {
  const targetRef = inputRef ? inputRef : selectRef
  const rect = targetRef.value.getBoundingClientRect()

  let boundaryTop, boundaryBottom
  const boundaryEl = formContext?.formRef?.value
  if (boundaryEl) {
    const boundaryRect = boundaryEl.getBoundingClientRect()
    boundaryTop = boundaryRect.top
    boundaryBottom = boundaryRect.bottom
  } else {
    boundaryTop = 0
    boundaryBottom = window.innerHeight
  }

  const distanceTop = rect.top - boundaryTop
  const distanceBottom = boundaryBottom - rect.bottom

  const left = rect.left
  const width = rect.width

  if (distanceTop < distanceBottom) {
    length.value = distanceBottom * 0.9
    optionContainerStyle.value = {
      top: `${rect.bottom + 4}px`,
      left: `${left}px`,
      width: `${width}px`,
    }
  } else {
    length.value = distanceTop * 0.9
    optionContainerStyle.value = {
      bottom: `${window.innerHeight - rect.top + 4}px`,
      left: `${left}px`,
      width: `${width}px`,
    }
  }
}

const onClick = () => {
  isVisible.value = !isVisible.value
}

const close = () => {
  isVisible.value = false
}

let resizeObserver = null

watch(isVisible, (bool) => {
  if (bool) {
    calculateOptionArea()
    resizeObserver = new ResizeObserver(calculateOptionArea)
    resizeObserver.observe(document.body)
  } else {
    resizeObserver?.disconnect()
    resizeObserver = null
  }
})

onMounted(() => {
  calculateOptionArea()
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})

provide('Select', selectContext)
</script>
